import { ApiClient } from '@api/api-configuration';
import type { PartnerDebt, RecentPaymentsResponse } from '@api/READ_ONLY/billing_api/Api';
import { BILLING_QUERY_KEYS } from '@app/common/queryKeys';
import Text from '@app/components/ui/Text/Text';
import { getNumberFormattingLocaleByLanguage } from '@app/i18n/helpers';
import type { SupportedCurrencies } from '@app/utils/pricingCurrencyUtils';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ClientsWithDebtsCard from './ClientsWithDebtsCard';
import PartnersDebtsCard from './PartnersDebtsCard';
import type { ClientDebtRow, PartnerDebtRow, PartnerType } from './types';

const CARD_CLASS_NAME = 'col-span-12 2xl:col-span-6 p-0';

type BillingOverviewTablesSectionProps = {
  currency: SupportedCurrencies;
  onViewAllClients: () => void;
  onViewAllPartners: () => void;
};

const formatPaymentDate = (value: string | null | undefined) => {
  if (!value) {
    return '-';
  }

  // With the year: a payment is a thing that happened, and these tables reach back
  // far enough that "12/05" could be this May or last one.
  const parsedDate = dayjs(value);
  return parsedDate.isValid() ? parsedDate.format('DD/MM/YYYY') : value;
};

const PARTNER_TYPE_BY_DEBT_TYPE: Record<PartnerDebt['type'], PartnerType> = {
  doctor: 'doctor',
  caregiver: 'nurse',
  provider: 'provider',
};

/** Rows of an unknown partner type are dropped rather than shown as another type. */
export const toPartnerDebtRows = (items: PartnerDebt[]): PartnerDebtRow[] =>
  items.flatMap((item) => {
    const partnerType: PartnerType | undefined = PARTNER_TYPE_BY_DEBT_TYPE[item.type];
    if (!partnerType) {
      return [];
    }

    return [
      {
        key: item.key,
        partnerId: item.partnerId,
        partnerName: item.partnerName,
        debt: item.debt ?? 0,
        type: partnerType,
        lastPaymentDate: formatPaymentDate(item.lastTransactionDate),
      },
    ];
  });

const BillingOverviewTablesSection = ({
  currency,
  onViewAllClients,
  onViewAllPartners,
}: BillingOverviewTablesSectionProps) => {
  const { i18n } = useTranslation();
  const locale = useMemo(() => getNumberFormattingLocaleByLanguage(i18n.language), [i18n.language]);

  const {
    data: recentPaymentsData,
    isLoading: isRecentPaymentsInitialLoading,
    isFetching: isRecentPaymentsFetching,
  } = useQuery<RecentPaymentsResponse>({
    queryKey: [BILLING_QUERY_KEYS.OVERVIEW_RECENT_PAYMENTS],
    queryFn: async () => {
      const response = await ApiClient.billingApi.billing.getBillingRecentPayments({ limit: 5 });
      return response.data;
    },
    placeholderData: keepPreviousData,
  });

  const isRecentPaymentsLoading = isRecentPaymentsInitialLoading || isRecentPaymentsFetching;

  const clientDebts = useMemo<ClientDebtRow[]>(() => {
    return (recentPaymentsData?.clientsWithDebts ?? []).map((item) => ({
      key: item.key,
      customerId: item.customerId,
      clientName: item.clientName,
      balance: item.balance ?? 0,
      lastPaymentDate: formatPaymentDate(item.lastTransactionDate),
    }));
  }, [recentPaymentsData?.clientsWithDebts]);

  const partnerDebts = useMemo<PartnerDebtRow[]>(
    () => toPartnerDebtRows(recentPaymentsData?.partnersDebts ?? []),
    [recentPaymentsData?.partnersDebts]
  );

  return (
    <section className="w-full bg-slate-50">
      <div className="mb-6 flex flex-col gap-2">
        <Text type="h4" transKey="receivables-payables" />

        <Text type="body2" transKey="receivables-payables-description" color="#64748b" />
      </div>

      <div className="grid grid-cols-12 gap-6">
        <ClientsWithDebtsCard
          data={clientDebts}
          totalDebt={recentPaymentsData?.clientsWithDebtsTotalDebt ?? 0}
          currency={currency}
          locale={locale}
          isLoading={isRecentPaymentsLoading}
          onViewAllClick={onViewAllClients}
          className={CARD_CLASS_NAME}
        />

        <PartnersDebtsCard
          data={partnerDebts}
          totalDebt={recentPaymentsData?.partnersDebtsTotalDebt ?? 0}
          currency={currency}
          locale={locale}
          isLoading={isRecentPaymentsLoading}
          onViewAllClick={onViewAllPartners}
          className={CARD_CLASS_NAME}
        />
      </div>
    </section>
  );
};

export default BillingOverviewTablesSection;
